import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  RefreshCw,
  AlertTriangle,
  Trash2,
  Users,
  Anchor,
  UserX,
  CheckCircle2,
  Link2,
  ShieldAlert,
  Wrench,
  Award,
  Sparkles,
  ShieldCheck,
  Gem,
  Crown,
  CircleDot,
  Star,
  RotateCcw,
  ToggleRight,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  DataIntegrityScanResult, 
  scanDatabaseIntegrity, 
  deleteUserDocument, 
  deleteCruisesBatch, 
  deleteFriendLookupsBatch,
  scanUnlinkedCruises,
  massRenameUserCruises,
  massDeleteUserCruises,
  UnlinkedCruiseGroup
} from '../services/dataIntegrityService';
import {
  previewCarnivalMigration,
  executeCarnivalMigration,
  getCarnivalProgramMode,
  enableCarnivalRewardsMode,
  revertToLegacyVIFPMode,
  CarnivalMigrationPreview,
  CarnivalProgramModeStatus,
} from '../services/carnivalLoyaltyMigrationService';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const AdminDataIntegrity = () => {
  const { cruiseLines, getShipsForCruiseLine, refreshCruiseMetadata } = useCruiseMetadata();
  const [data, setData] = useState<DataIntegrityScanResult | null>(null);
  const [unlinkedCruises, setUnlinkedCruises] = useState<UnlinkedCruiseGroup[]>([]);
  const [orphanedUsers, setOrphanedUsers] = useState<any[]>([]);
  const [orphanedError, setOrphanedError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [relinkingTarget, setRelinkingTarget] = useState<Record<string, string>>({});

  // Carnival Rewards Migration State
  const [carnivalPreview, setCarnivalPreview] = useState<CarnivalMigrationPreview | null>(null);
  const [carnivalMode, setCarnivalMode] = useState<CarnivalProgramModeStatus | null>(null);
  const [carnivalLoading, setCarnivalLoading] = useState(false);
  const [carnivalMigrating, setCarnivalMigrating] = useState(false);
  const [carnivalToggling, setCarnivalToggling] = useState(false);

  const scanDatabase = async () => {
    setLoading(true);
    try {
      // 1. Scan Duplicate Users and Orphaned Cruises/Lookups
      const result = await scanDatabaseIntegrity();
      setData(result);
      
      // 2. Scan Unlinked User Cruises
      try {
        const unlinkedResult = await scanUnlinkedCruises(cruiseLines);
        setUnlinkedCruises(unlinkedResult);
      } catch (err) {
        console.error('Error scanning unlinked cruises:', err);
      }

      // 3. Scan Orphaned User Documents (Auth comparison)
      try {
        const getOrphanedUsersFn = httpsCallable<any, any[]>(functions, 'getOrphanedUsers');
        const orphanedResult = await getOrphanedUsersFn();
        setOrphanedUsers(orphanedResult.data);
        setOrphanedError(false);
      } catch (err) {
        console.warn('getOrphanedUsers function error (possibly not deployed yet):', err);
        setOrphanedUsers([]);
        setOrphanedError(true);
      }

      toast.success('Database scan complete');
    } catch (error) {
      console.error('Error scanning database:', error);
      toast.error('Failed to scan database');
    } finally {
      setLoading(false);
    }
  };

  const scanCarnival = async () => {
    setCarnivalLoading(true);
    try {
      const [res, mode] = await Promise.all([
        previewCarnivalMigration(),
        getCarnivalProgramMode()
      ]);
      setCarnivalPreview(res);
      setCarnivalMode(mode);
    } catch (error) {
      console.error('Error scanning Carnival accounts:', error);
      toast.error('Failed to scan Carnival accounts');
    } finally {
      setCarnivalLoading(false);
    }
  };

  const handleExecuteCarnivalMigration = async () => {
    if (!carnivalPreview || carnivalPreview.pendingMigrationCount === 0) return;
    if (!window.confirm(`Are you sure you want to preload grandfathered status for ${carnivalPreview.pendingMigrationCount} Carnival member(s)? This will save their legacy VIFP sailing days in 'legacyPoints' and set grandfathered tiers in Firestore.`)) {
      return;
    }
    setCarnivalMigrating(true);
    try {
      const result = await executeCarnivalMigration();
      if (result.success) {
        toast.success(`Successfully migrated ${result.migratedCount} Carnival account(s)!`);
      } else {
        toast.error(`Migration completed with errors: ${result.errors.join(', ')}`);
      }
      await scanCarnival();
    } catch (error) {
      console.error('Error executing Carnival migration:', error);
      toast.error('Carnival migration failed');
    } finally {
      setCarnivalMigrating(false);
    }
  };

  const handleEnableCarnivalRewards = async () => {
    if (!window.confirm(
      'Switch Carnival to Carnival Rewards (Phase 3)?\n\n' +
      '• cruiseMetadata/carnival will update to Carnival Rewards (stars, manual input, 2-year rolling cycle)\n' +
      '• Grandfathered Platinum accounts receive their 10,000 Star biennial bonus (active balance >= 10,000 Stars)\n' +
      '• Other tiers start at 0 Stars for the 2-year active cycle\n' +
      '• Legacy VIFP sailing days remain safely preserved in legacyPoints\n' +
      '• Grandfathered status remains 100% active and protected\n\n' +
      'You can revert back to legacy VIFP at any time using this switch.'
    )) {
      return;
    }

    setCarnivalToggling(true);
    try {
      const result = await enableCarnivalRewardsMode();
      if (result.success) {
        toast.success(`Carnival Rewards activated! Updated ${result.updatedUsersCount} user account(s).`);
        await refreshCruiseMetadata();
        await scanCarnival();
      } else {
        toast.error(`Activation failed: ${result.errors.join(', ')}`);
      }
    } catch (error: any) {
      console.error('Error activating Carnival Rewards:', error);
      toast.error('Failed to activate Carnival Rewards');
    } finally {
      setCarnivalToggling(false);
    }
  };

  const handleRevertToLegacyVIFP = async () => {
    if (!window.confirm(
      'Revert Carnival to Legacy VIFP Club?\n\n' +
      '• cruiseMetadata/carnival will revert to VIFP Club (points, automatic days-based calculation)\n' +
      '• User active status will be restored back to their historical sailing days from legacyPoints\n' +
      '• Grandfathered status and legacyPoints remain preserved in Firestore\n\n' +
      'Continue?'
    )) {
      return;
    }

    setCarnivalToggling(true);
    try {
      const result = await revertToLegacyVIFPMode();
      if (result.success) {
        toast.success(`Reverted to legacy VIFP Club! Restored ${result.restoredUsersCount} user account(s).`);
        await refreshCruiseMetadata();
        await scanCarnival();
      } else {
        toast.error(`Reversion failed: ${result.errors.join(', ')}`);
      }
    } catch (error: any) {
      console.error('Error reverting to legacy VIFP:', error);
      toast.error('Failed to revert to legacy VIFP');
    } finally {
      setCarnivalToggling(false);
    }
  };

  useEffect(() => {
    if (cruiseLines.length > 0) {
      scanDatabase();
      scanCarnival();
    }
  }, [cruiseLines]);

  const handleDeleteUser = async (uid: string) => {
    if (!window.confirm(`Are you absolutely sure you want to delete user document ${uid}? This cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await deleteUserDocument(uid);
      toast.success('User document deleted');
      await scanDatabase();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleCleanCruises = async () => {
    if (!data || data.orphanedCruises.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete all ${data.orphanedCruises.length} orphaned cruises?`)) {
      return;
    }

    setDeleting(true);
    try {
      const ids = data.orphanedCruises.map(c => c.id);
      await deleteCruisesBatch(ids);
      toast.success(`Deleted ${ids.length} orphaned cruises`);
      await scanDatabase();
    } catch (error) {
      console.error('Error deleting cruises:', error);
      toast.error('Failed to delete orphaned cruises');
    } finally {
      setDeleting(false);
    }
  };

  const handleCleanLookups = async () => {
    if (!data || data.orphanedLookups.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete all ${data.orphanedLookups.length} orphaned friend profiles?`)) {
      return;
    }

    setDeleting(true);
    try {
      const ids = data.orphanedLookups.map(l => l.id);
      await deleteFriendLookupsBatch(ids);
      toast.success(`Deleted ${ids.length} orphaned friend profiles`);
      await scanDatabase();
    } catch (error) {
      console.error('Error deleting friend lookups:', error);
      toast.error('Failed to delete orphaned profiles');
    } finally {
      setDeleting(false);
    }
  };

  const handleRelinkCruises = async (group: UnlinkedCruiseGroup, officialShipName: string) => {
    if (!officialShipName) return;
    if (!window.confirm(`Are you sure you want to mass rename all ${group.count} user cruises from "${group.shipName}" to "${officialShipName}"?`)) {
      return;
    }
    setDeleting(true);
    try {
      const count = await massRenameUserCruises(group.cruiseLine, group.shipName, officialShipName);
      toast.success(`Successfully re-linked ${count} cruises to "${officialShipName}"`);
      await scanDatabase();
    } catch (error) {
      console.error('Error re-linking cruises:', error);
      toast.error('Failed to re-link cruises');
    } finally {
      setDeleting(false);
    }
  };



  const handleMassDeleteCruises = async (group: UnlinkedCruiseGroup) => {
    if (!window.confirm(`Are you sure you want to mass delete all ${group.count} cruises for ship "${group.shipName}" under "${group.cruiseLine}"? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const count = await massDeleteUserCruises(group.cruiseLine, group.shipName);
      toast.success(`Deleted ${count} cruises`);
      await scanDatabase();
    } catch (error) {
      console.error('Error deleting user cruises:', error);
      toast.error('Failed to delete user cruises');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteOrphanedUser = async (uid: string) => {
    if (!window.confirm(`Are you sure you want to delete orphaned Firestore document for UID ${uid}?`)) {
      return;
    }
    setDeleting(true);
    try {
      const deleteOrphanedUsersFn = httpsCallable<{ uids: string[] }, any>(functions, 'deleteOrphanedUsers');
      await deleteOrphanedUsersFn({ uids: [uid] });
      toast.success('Deleted orphaned user document');
      await scanDatabase();
    } catch (error) {
      console.error('Error deleting orphaned user:', error);
      toast.error('Failed to delete orphaned user');
    } finally {
      setDeleting(false);
    }
  };

  const handleCleanAllOrphanedUsers = async () => {
    if (orphanedUsers.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete all ${orphanedUsers.length} orphaned Firestore user documents?`)) {
      return;
    }
    setDeleting(true);
    try {
      const deleteOrphanedUsersFn = httpsCallable<{ uids: string[] }, any>(functions, 'deleteOrphanedUsers');
      const uids = orphanedUsers.map(u => u.uid);
      await deleteOrphanedUsersFn({ uids });
      toast.success(`Deleted ${uids.length} orphaned user documents`);
      await scanDatabase();
    } catch (error) {
      console.error('Error cleaning orphaned users:', error);
      toast.error('Failed to clean orphaned users');
    } finally {
      setDeleting(false);
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label="Back to Admin Dashboard"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Wrench className="h-8 w-8 text-amber-600" />
            Data Integrity Scanner
          </h1>
          <p className="text-gray-600 mt-1">Identify and clean up duplicate or orphaned records</p>
        </div>
        <div className="ml-auto flex items-center">
          <button
            onClick={scanDatabase}
            disabled={loading || deleting}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Scanning...' : 'Scan Database'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
        </div>
      ) : data ? (
        <div className="space-y-8">
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className={`rounded-lg border p-4 shadow-sm ${data.duplicateUsers.length > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Duplicate Users</p>
                  <p className={`mt-1 text-2xl font-bold ${data.duplicateUsers.length > 0 ? 'text-orange-700' : 'text-gray-900'}`}>
                    {data.duplicateUsers.length}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${data.duplicateUsers.length > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                  {data.duplicateUsers.length > 0 ? <AlertTriangle className="h-5 w-5 text-orange-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
              </div>
            </div>

            <div className={`rounded-lg border p-4 shadow-sm ${data.orphanedCruises.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Orphaned Cruises</p>
                  <p className={`mt-1 text-2xl font-bold ${data.orphanedCruises.length > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                    {data.orphanedCruises.length}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${data.orphanedCruises.length > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  {data.orphanedCruises.length > 0 ? <AlertTriangle className="h-5 w-5 text-red-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
              </div>
            </div>

            <div className={`rounded-lg border p-4 shadow-sm ${data.orphanedLookups.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Orphaned Profiles</p>
                  <p className={`mt-1 text-2xl font-bold ${data.orphanedLookups.length > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                    {data.orphanedLookups.length}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${data.orphanedLookups.length > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  {data.orphanedLookups.length > 0 ? <AlertTriangle className="h-5 w-5 text-red-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
              </div>
            </div>

            <div className={`rounded-lg border p-4 shadow-sm ${unlinkedCruises.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Unlinked Ships</p>
                  <p className={`mt-1 text-2xl font-bold ${unlinkedCruises.length > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                    {unlinkedCruises.length}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${unlinkedCruises.length > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  {unlinkedCruises.length > 0 ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
              </div>
            </div>

            <div className={`rounded-lg border p-4 shadow-sm ${orphanedUsers.length > 0 ? 'bg-rose-50 border-rose-200' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Orphaned Accounts</p>
                  <p className={`mt-1 text-2xl font-bold ${orphanedUsers.length > 0 ? 'text-rose-700' : 'text-gray-900'}`}>
                    {orphanedUsers.length}
                  </p>
                </div>
                <div className={`p-2 rounded-full ${orphanedUsers.length > 0 ? 'bg-rose-100' : 'bg-gray-100'}`}>
                  {orphanedUsers.length > 0 ? <ShieldAlert className="h-5 w-5 text-rose-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
              </div>
            </div>
          </div>

          {/* Carnival Rewards Migration & Live Switch Section */}
          <div className="rounded-xl border border-indigo-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-white px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                    <Award className="h-5 w-5" />
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">
                    Carnival Rewards Migration & Switch
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Manage grandfathered status preloading and live toggle between legacy <strong>VIFP Club</strong> and new <strong>Carnival Rewards</strong>.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={scanCarnival}
                  disabled={carnivalLoading || carnivalMigrating || carnivalToggling}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${carnivalLoading ? 'animate-spin text-indigo-600' : ''}`} />
                  {carnivalLoading ? 'Scanning...' : 'Refresh Scan'}
                </button>

                {carnivalPreview && carnivalPreview.pendingMigrationCount > 0 && (
                  <button
                    type="button"
                    onClick={handleExecuteCarnivalMigration}
                    disabled={carnivalMigrating || carnivalLoading || carnivalToggling}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {carnivalMigrating ? 'Preloading...' : `Preload ${carnivalPreview.pendingMigrationCount} Account${carnivalPreview.pendingMigrationCount !== 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>

            {/* Live Program Mode Switch Banner */}
            <div className="p-4 bg-gradient-to-r from-indigo-50/70 via-blue-50/50 to-white border-b border-indigo-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className={`p-2.5 rounded-xl shrink-0 ${carnivalMode?.isCarnivalRewardsActive ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {carnivalMode?.isCarnivalRewardsActive ? (
                    <Sparkles className="h-5 w-5" />
                  ) : (
                    <Clock className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm">Firestore Program Mode:</span>
                    {carnivalMode?.isCarnivalRewardsActive ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        Carnival Rewards (Stars / Manual / 2-Yr Cycle)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300">
                        <Clock className="h-3.5 w-3.5 text-blue-600" />
                        Legacy VIFP Club (Days / Points / Auto)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {carnivalMode?.isCarnivalRewardsActive
                      ? 'Carnival Rewards is LIVE in Firestore. Calculations use manual entry of Stars with grandfathered tier protection. You can revert back to legacy VIFP at any time.'
                      : 'Legacy VIFP Club is active in Firestore. Calculations use days sailed. Turn on Carnival Rewards to test the new spending-based system in the app.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {carnivalMode?.isCarnivalRewardsActive ? (
                  <button
                    type="button"
                    onClick={handleRevertToLegacyVIFP}
                    disabled={carnivalToggling || carnivalLoading}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    <RotateCcw className={`h-4 w-4 ${carnivalToggling ? 'animate-spin' : ''}`} />
                    {carnivalToggling ? 'Reverting...' : 'Revert to Legacy VIFP'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleEnableCarnivalRewards}
                    disabled={carnivalToggling || carnivalLoading}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    <ToggleRight className="h-4 w-4" />
                    {carnivalToggling ? 'Switching...' : 'Switch to Carnival Rewards'}
                  </button>
                )}
              </div>
            </div>

            {/* Metrics & Breakdown */}
            {carnivalPreview && (
              <div className="p-5 bg-white border-b border-gray-100 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <span className="text-xs font-medium text-gray-500 block">Carnival Accounts</span>
                    <span className="text-xl font-bold text-gray-900 mt-0.5 block">{carnivalPreview.totalCarnivalMembers}</span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <span className="text-xs font-medium text-amber-700 block">Pending Preload</span>
                    <span className="text-xl font-bold text-amber-900 mt-0.5 block">{carnivalPreview.pendingMigrationCount}</span>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <span className="text-xs font-medium text-emerald-700 block">Preloaded & Safe</span>
                    <span className="text-xl font-bold text-emerald-900 mt-0.5 block">{carnivalPreview.alreadyMigratedCount}</span>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <span className="text-xs font-medium text-indigo-700 block">Grandfathered Tiers</span>
                    <div className="flex items-center gap-1.5 mt-1 text-xs font-semibold flex-wrap">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">
                        <Gem className="h-3 w-3 text-purple-600" /> {carnivalPreview.breakdown.diamond}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded">
                        <Award className="h-3 w-3 text-indigo-600" /> {carnivalPreview.breakdown.platinum}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
                        <Crown className="h-3 w-3 text-amber-600" /> {carnivalPreview.breakdown.gold}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-800 rounded">
                        <CircleDot className="h-3 w-3 text-red-600" /> {carnivalPreview.breakdown.red}
                      </span>
                    </div>
                  </div>
                </div>

                {carnivalPreview.users.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Legacy VIFP Days</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Grandfathered Tier</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Active Balance</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {carnivalPreview.users.map((u) => {
                          const tierBadgeClass =
                            u.proposedTier === 'Diamond'
                              ? 'bg-purple-100 text-purple-800 border-purple-200'
                              : u.proposedTier === 'Platinum'
                              ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                              : u.proposedTier === 'Gold'
                              ? 'bg-amber-100 text-amber-800 border-amber-200'
                              : 'bg-red-100 text-red-800 border-red-200';

                          return (
                            <tr key={u.uid} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-2.5">
                                <div className="font-semibold text-gray-900">{u.displayName}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                              </td>
                              <td className="px-4 py-2.5 font-medium text-gray-800">
                                {u.legacyPoints !== undefined ? `${u.legacyPoints} days` : `${u.currentStatus} days`}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${tierBadgeClass}`}>
                                  {u.proposedTier}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-600">
                                {u.proposedExpiry === 'lifetime' ? (
                                  <span className="font-semibold text-purple-700">Permanent Lifetime</span>
                                ) : u.proposedExpiry === '2028-05-31' ? (
                                  <span>May 31, 2028 (+10k boost)</span>
                                ) : (
                                  <span>Dec 31, 2028</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                {carnivalMode?.isCarnivalRewardsActive ? (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-semibold text-gray-900">
                                      {u.currentStatus.toLocaleString()} Stars
                                    </span>
                                    {u.proposedTier === 'Platinum' && (
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                        u.hasPlatinumBoost
                                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                                      }`}>
                                        <Sparkles className="h-3 w-3 mr-0.5" />
                                        {u.hasPlatinumBoost ? '10k boost active' : '10k boost pending'}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-700 font-medium">
                                    {u.currentStatus} points (days)
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                {u.isAlreadyMigrated ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Preloaded
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                    <AlertTriangle className="h-3 w-3" />
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-gray-500">
                    No Carnival loyalty members found in the database.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Duplicate Users Section */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-600" />
                Duplicate User Accounts
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Multiple documents in the <code>users</code> collection sharing the exact same email address.
              </p>
            </div>
            
            <div className="p-0">
              {data.duplicateUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                  <p>No duplicate user records found.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {data.duplicateUsers.map((group, idx) => (
                    <div key={idx} className="p-5">
                      <div className="mb-3">
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-sm font-medium text-gray-800">
                          {group.email}
                        </span>
                        <span className="ml-2 text-sm text-red-600 font-medium">{group.records.length} conflicting records</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.records.map((record, rIdx) => (
                          <div key={record.uid} className={`relative rounded-lg border ${rIdx === 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'} p-4`}>
                            {rIdx === 0 && <span className="absolute -top-2.5 -right-2.5 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20 shadow-sm">Likely Active</span>}
                            {rIdx !== 0 && <span className="absolute -top-2.5 -right-2.5 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-600/20 shadow-sm">Likely Ghost</span>}
                            
                            <p className="text-sm font-semibold text-gray-900">{record.displayName}</p>
                            <p className="text-xs text-gray-500 font-mono mt-1 mb-3 shrink-0 break-all">UID: {record.uid}</p>
                            
                            <div className="flex justify-between items-center text-sm mb-4">
                              <span className="text-gray-600">Created:</span>
                              <span className="font-medium text-gray-900">{record.createdAt}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm mb-4">
                              <span className="text-gray-600">Cruises:</span>
                              <span className={`font-medium ${record.cruiseCount > 0 ? 'text-indigo-600' : 'text-gray-900'}`}>{record.cruiseCount}</span>
                            </div>
                            
                            <button
                              onClick={() => handleDeleteUser(record.uid)}
                              disabled={deleting}
                              className="w-full flex justify-center items-center gap-2 rounded bg-white px-3 py-1.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete This Record
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Orphaned Cruises Section */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Anchor className="h-5 w-5 text-indigo-600" />
                  Orphaned Cruises
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Cruises whose owning <code>userId</code> no longer exists in the <code>users</code> collection.
                </p>
              </div>
              {data.orphanedCruises.length > 0 && (
                <button
                  onClick={handleCleanCruises}
                  disabled={deleting}
                  className="flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Clean All ({data.orphanedCruises.length})
                </button>
              )}
            </div>
            
            <div className="p-0">
              {data.orphanedCruises.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                  <p>No orphaned cruises found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cruise Line</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departure</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missing UserID</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {data.orphanedCruises.map(cruise => (
                        <tr key={cruise.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{cruise.cruiseLine}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{cruise.shipName}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{cruise.departureDate}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-red-500 font-mono">{cruise.userId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Orphaned Lookups Section */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <UserX className="h-5 w-5 text-indigo-600" />
                  Orphaned Friend Profiles
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Entries in <code>friendLookup</code> mapping to a user that no longer exists.
                </p>
              </div>
              {data.orphanedLookups.length > 0 && (
                <button
                  onClick={handleCleanLookups}
                  disabled={deleting}
                  className="flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Clean All ({data.orphanedLookups.length})
                </button>
              )}
            </div>
            
            <div className="p-0">
              {data.orphanedLookups.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                  <p>No orphaned friend profiles found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lookup ID</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missing UserID</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {data.orphanedLookups.map(lookup => (
                        <tr key={lookup.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">{lookup.id}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{lookup.username}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{lookup.email}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-red-500 font-mono">{lookup.uid}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Unlinked User Ships Section */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Link2 className="h-5 w-5 text-indigo-600" />
                Unlinked User Ships
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Cruises added by users containing ship names that do not exist in the official cruise line metadata.
              </p>
            </div>
            
            <div className="p-0">
              {unlinkedCruises.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                  <p>All user cruises are linked to official ship names!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cruise Line</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unlinked Ship Name</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affected Cruises</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {unlinkedCruises.map((group, idx) => {
                        const shipsObj = getShipsForCruiseLine(group.cruiseLine);
                        const officialShips = [...(shipsObj.active || []), ...(shipsObj.retired || [])];
                        const targetShip = relinkingTarget[`${group.cruiseLine}_${group.shipName}`] || '';

                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{group.cruiseLine}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-red-600 font-semibold">{group.shipName}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">{group.count}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm space-y-2 md:space-y-0 md:space-x-3 flex flex-col md:flex-row items-stretch md:items-center">
                              {officialShips.length > 0 && (
                                <div className="flex items-center gap-2">
                                  <select
                                    value={targetShip}
                                    onChange={(e) => setRelinkingTarget(prev => ({
                                      ...prev,
                                      [`${group.cruiseLine}_${group.shipName}`]: e.target.value
                                    }))}
                                    className="rounded border border-gray-300 text-xs px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  >
                                    <option value="">-- Re-link to Ship --</option>
                                    {officialShips.map(s => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleRelinkCruises(group, targetShip)}
                                    disabled={deleting || !targetShip}
                                    className="rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1 disabled:opacity-50 transition-colors"
                                  >
                                    Re-link
                                  </button>
                                </div>
                              )}
                              
                              <button
                                onClick={() => handleMassDeleteCruises(group)}
                                disabled={deleting}
                                className="rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1 transition-colors flex items-center gap-1 justify-center"
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete ({group.count})
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Orphaned Firestore User Accounts Section */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-indigo-600" />
                  Orphaned User Accounts
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Firestore documents in the <code>users</code> collection whose corresponding account was deleted from Firebase Auth.
                </p>
              </div>
              {orphanedUsers.length > 0 && (
                <button
                  onClick={handleCleanAllOrphanedUsers}
                  disabled={deleting}
                  className="flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Clean All ({orphanedUsers.length})
                </button>
              )}
            </div>
            
            <div className="p-0">
              {orphanedUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                  {orphanedError ? (
                    <>
                      <AlertTriangle className="h-10 w-10 text-amber-500 mb-2" />
                      <p>Cloud Function getOrphanedUsers unavailable or failed.</p>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                      <p>No orphaned Firestore user accounts found.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {orphanedUsers.map(user => (
                        <tr key={user.uid} className="hover:bg-gray-50">
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">{user.displayName}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">{user.uid}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{user.createdAt}</td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleDeleteOrphanedUser(user.uid)}
                              disabled={deleting}
                              className="text-red-600 hover:text-red-900 font-semibold flex items-center gap-1 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      ) : null}
    </motion.div>
  );
};

export default AdminDataIntegrity;
